import type { QueryClient } from '@tanstack/react-query';

import { discussionsQueryKeys } from './discussions';

export const DISCUSSION_WS_EVENT = 'discussion';
const LEGACY_DISCUSSION_WS_EVENT = 'clinical_discussion';

const DISCUSSION_WS_EVENTS = new Set([DISCUSSION_WS_EVENT, LEGACY_DISCUSSION_WS_EVENT]);

export type DiscussionWsPayload = {
  action?: 'created' | 'replied' | 'status_changed';
  study_instance_uid?: string;
  discussion_id?: string;
  root_id?: string;
  author_id?: string;
  target_type?: string;
  target_ref?: string;
};

function normalizeDiscussionPayload(
  raw: Record<string, unknown>
): DiscussionWsPayload | null {
  const studyUid = raw.study_instance_uid;
  if (typeof studyUid !== 'string' || !studyUid) {
    return null;
  }

  return {
    action: raw.action as DiscussionWsPayload['action'],
    study_instance_uid: studyUid,
    discussion_id:
      typeof raw.discussion_id === 'string' ? raw.discussion_id : undefined,
    root_id: typeof raw.root_id === 'string' ? raw.root_id : undefined,
    author_id: typeof raw.author_id === 'string' ? raw.author_id : undefined,
    target_type:
      typeof raw.target_type === 'string' ? raw.target_type : undefined,
    target_ref: typeof raw.target_ref === 'string' ? raw.target_ref : undefined,
  };
}

function payloadFromEnvelope(data: unknown): DiscussionWsPayload | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return normalizeDiscussionPayload(data as Record<string, unknown>);
}

export function extractDiscussionWsPayload(
  messageData: Record<string, unknown>
): DiscussionWsPayload | null {
  const event = messageData.event as string | undefined;

  if (event && DISCUSSION_WS_EVENTS.has(event)) {
    return payloadFromEnvelope(messageData.data);
  }

  // Unified notification envelope carrying discussion metadata in data.data
  if (event === 'notification') {
    const notification = messageData.data;
    if (notification && typeof notification === 'object') {
      const nested = (notification as Record<string, unknown>).data;
      if (nested && typeof nested === 'object') {
        const nestedRecord = nested as Record<string, unknown>;
        if (
          nestedRecord.event === DISCUSSION_WS_EVENT ||
          nestedRecord.event === LEGACY_DISCUSSION_WS_EVENT ||
          nestedRecord.study_instance_uid
        ) {
          return normalizeDiscussionPayload(nestedRecord);
        }
      }
    }
  }

  return null;
}

function refetchDiscussionQueries(
  queryClient: QueryClient,
  studyUid: string,
  threadId?: string
) {
  void queryClient.refetchQueries({
    queryKey: discussionsQueryKeys.summary(studyUid),
    type: 'all',
  });
  void queryClient.refetchQueries({
    queryKey: discussionsQueryKeys.list(studyUid),
    type: 'all',
  });

  if (threadId) {
    void queryClient.refetchQueries({
      queryKey: discussionsQueryKeys.thread(threadId),
      type: 'all',
    });
  }
}

/** Refetch collaboration queries (keeps previous data visible while updating). */
export function handleDiscussionWs(
  messageData: Record<string, unknown>,
  queryClient: QueryClient
): boolean {
  const payload = extractDiscussionWsPayload(messageData);
  if (!payload?.study_instance_uid) {
    return false;
  }

  const threadId = payload.root_id ?? payload.discussion_id;
  refetchDiscussionQueries(queryClient, payload.study_instance_uid, threadId);

  return true;
}

export function refetchDiscussionsForStudy(
  queryClient: QueryClient,
  studyUid: string,
  threadId?: string
) {
  refetchDiscussionQueries(queryClient, studyUid, threadId);
}
